import { setActiveAppearance } from './appearance/active-appearance';
import {
  type Appearance,
  applyAppearance,
  DEFAULT_APPEARANCE,
  normalizeAppearance,
} from './appearance/appearance';
import { readDevicePreference, writeDevicePreference } from './device-preferences';

export const APP_SETTINGS_KEY = 'crowdex.settings';

/** Dispatched once a setting changed, for surfaces that read them (the shell, the composer). */
export const SETTINGS_CHANGED_EVENT = 'crowdex-settings-changed';

export type AgentRuntimeOverride = {
  args?: string[];
  command?: string;
  launchCommand?: string;
};

export type AppSettings = {
  agentIds: string[] | null;
  agentRuntimeOverrides: Record<string, AgentRuntimeOverride>;
  /** The code theme and chrome of the light and the dark variant. */
  appearance: Appearance;
  autoApprove: boolean;
  autoSave: boolean;
  compactMode: boolean;
  defaultAgent: string;
  displayName: string;
  /** New pull requests start as drafts. */
  draftPullRequests: boolean;
  editor: string;
  language: string;
  /** After a reply, the composer suggests the next message, which Tab accepts. */
  messagePredictions: boolean;
  /** The session's pull request status is shown next to its title. */
  pullRequestStatusInHeader: boolean;
  theme: string;
};

export const DEFAULT_APP_SETTINGS: AppSettings = {
  agentIds: null,
  agentRuntimeOverrides: {},
  appearance: DEFAULT_APPEARANCE,
  autoApprove: false,
  autoSave: true,
  compactMode: false,
  defaultAgent: 'auto',
  displayName: '',
  draftPullRequests: true,
  editor: 'vscode',
  language: 'system',
  messagePredictions: true,
  pullRequestStatusInHeader: true,
  theme: 'dark',
};

export function readAppSettings(): AppSettings {
  try {
    const stored = readDevicePreference(APP_SETTINGS_KEY);
    const parsed = JSON.parse(stored ?? '{}') as Partial<AppSettings>;
    const defaultAgent =
      typeof parsed.defaultAgent === 'string'
        ? parsed.defaultAgent
        : DEFAULT_APP_SETTINGS.defaultAgent;
    const agentIds =
      parsed.agentIds === undefined || parsed.agentIds === null ? null : parsed.agentIds;
    return {
      defaultAgent,
      agentIds: agentIds ?? null,
      agentRuntimeOverrides: parsed.agentRuntimeOverrides ?? {},
      appearance: normalizeAppearance(parsed.appearance),
      autoApprove:
        typeof parsed.autoApprove === 'boolean'
          ? parsed.autoApprove
          : DEFAULT_APP_SETTINGS.autoApprove,
      autoSave:
        typeof parsed.autoSave === 'boolean' ? parsed.autoSave : DEFAULT_APP_SETTINGS.autoSave,
      compactMode:
        typeof parsed.compactMode === 'boolean'
          ? parsed.compactMode
          : DEFAULT_APP_SETTINGS.compactMode,
      displayName:
        typeof parsed.displayName === 'string'
          ? parsed.displayName
          : DEFAULT_APP_SETTINGS.displayName,
      draftPullRequests:
        typeof parsed.draftPullRequests === 'boolean'
          ? parsed.draftPullRequests
          : DEFAULT_APP_SETTINGS.draftPullRequests,
      editor: typeof parsed.editor === 'string' ? parsed.editor : DEFAULT_APP_SETTINGS.editor,
      language:
        typeof parsed.language === 'string' ? parsed.language : DEFAULT_APP_SETTINGS.language,
      messagePredictions:
        typeof parsed.messagePredictions === 'boolean'
          ? parsed.messagePredictions
          : DEFAULT_APP_SETTINGS.messagePredictions,
      pullRequestStatusInHeader:
        typeof parsed.pullRequestStatusInHeader === 'boolean'
          ? parsed.pullRequestStatusInHeader
          : DEFAULT_APP_SETTINGS.pullRequestStatusInHeader,
      theme: typeof parsed.theme === 'string' ? parsed.theme : DEFAULT_APP_SETTINGS.theme,
    };
  } catch {
    return DEFAULT_APP_SETTINGS;
  }
}

export function writeAppSettings(settings: AppSettings): Promise<void> {
  return writeDevicePreference(APP_SETTINGS_KEY, JSON.stringify(settings));
}

export function resolveDesktopTheme(theme: string): 'desktop-dark' | 'desktop-light' {
  if (theme === 'dark') return 'desktop-dark';
  if (theme === 'light') return 'desktop-light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'desktop-dark'
    : 'desktop-light';
}

/** Dispatched with the theme and density `applyAppSettings` applied, so surfaces follow a preview. */
export const APPEARANCE_APPLIED_EVENT = 'crowdex-appearance-applied';
export type AppliedAppearance = Pick<AppSettings, 'theme' | 'compactMode' | 'appearance'>;

export function applyAppSettings(settings: AppSettings): void {
  const dark = resolveDesktopTheme(settings.theme) === 'desktop-dark';
  const desktopTheme = dark ? 'desktop-dark' : 'desktop-light';
  document.documentElement.classList.toggle('light', !dark);
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  document.body.dataset['theme'] = desktopTheme;
  document.body.dataset['compact'] = String(settings.compactMode);
  applyAppearance(settings.appearance);
  setActiveAppearance(settings.appearance, dark ? 'dark' : 'light');
  document.documentElement.lang =
    settings.language === 'system' ? navigator.language : settings.language;
  window.dispatchEvent(
    new CustomEvent<AppliedAppearance>(APPEARANCE_APPLIED_EVENT, {
      detail: {
        theme: settings.theme,
        compactMode: settings.compactMode,
        appearance: settings.appearance,
      },
    }),
  );
}
